import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import { DEFAULT_TAB, findTab, writeStickyTab } from './tabConfig';
import { useWorkspaceDevice } from './workspaceContext';
import OverviewTab from './OverviewTab';
import TabPlaceholder from './TabPlaceholder';
import DeviceNotesPanel from '../DeviceNotesPanel';
import DevicePhotosPanel from '../DevicePhotosPanel';
import DeviceDebugPanel from '../DeviceDebugPanel';
import AnalyzePanel from '../AnalyzePanel';
import DeviceSettingsPanel from '../DeviceSettingsPanel';
import DeviceAppClaimsPanel from '../DeviceAppClaimsPanel';
import DeviceAppGraphsPanel from '../DeviceAppGraphsPanel';
import MissingTab from './MissingTab';

// FS-749 — the Experimental tab carries KaTeX (its code, CSS and fonts), so it
// is split into its own chunk and loaded only when the tab is opened.
const ExperimentalTab = lazy(() => import('./ExperimentalTab'));

/**
 * Plan 94 - resolves the route `:tab` to a tab component, all bound to the
 * workspace's resolved device.
 *
 * - Unknown tab keys fall back to the default tab (replace, so back-button works).
 * - Known-but-unpermitted tabs render an access-denied card rather than redirect.
 * - The viewed tab is recorded as the sticky preference.
 *
 * Phase 2 wires the real per-device panels in; remaining tabs keep a placeholder.
 */

/** Wrap bare tool panels in the standard workspace card. */
const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-card border border-border rounded-lg shadow-md p-6">{children}</div>
);

const DeviceWorkspaceTab: React.FC = () => {
  const { deviceId, tab } = useParams<{ deviceId: string; tab: string }>();
  const { search } = useLocation();
  const { hasPermission } = useAuth();
  const { device, refetchDevice } = useWorkspaceDevice();
  const def = findTab(tab);

  useEffect(() => {
    if (def) writeStickyTab(def.key);
  }, [def]);

  if (!def) {
    // Keep the query string: an unknown tab shouldn't drop the carried list.
    return <Navigate to={`/devices/${deviceId}/${DEFAULT_TAB}${search}`} replace />;
  }

  if (def.perm && !hasPermission(def.perm)) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-md p-6 text-center">
        <h3 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h3>
        <p className="text-sm text-muted-foreground">
          The {def.label} tab requires the{' '}
          <code className="px-1.5 py-0.5 bg-gray-100 rounded-sm font-mono text-xs">{def.perm}</code>{' '}
          permission.
        </p>
      </div>
    );
  }

  switch (def.key) {
    case 'overview':
      return <OverviewTab />;
    case 'notes':
      return (
        <Card>
          <DeviceNotesPanel device={device} />
        </Card>
      );
    case 'photos':
      return (
        <Card>
          <DevicePhotosPanel device={device} />
        </Card>
      );
    case 'debug':
      // Panel renders its own multi-section layout (debug data, S3 bundles, logs).
      return <DeviceDebugPanel device={device} />;
    case 'analysis':
      // Panel renders its own page chrome; route device seeds the selection.
      return <AnalyzePanel device={device} />;
    case 'settings':
      return (
        <Card>
          <DeviceSettingsPanel device={device} onDeviceChanged={refetchDevice} />
        </Card>
      );
    case 'claims':
      return (
        <Card>
          <DeviceAppClaimsPanel device={device} />
        </Card>
      );
    case 'appgraphs':
      return (
        <Card>
          <DeviceAppGraphsPanel device={device} />
        </Card>
      );
    case 'missing':
      // Plan 123 — the tab renders its own card and reads the device off the
      // workspace context, like the Overview tab.
      return <MissingTab />;
    case 'experimental':
      // FS-749 (Plan 128) — renders its own cards; reads the device off the
      // workspace context.
      return (
        <Suspense
          fallback={
            <div className="bg-card border border-border rounded-lg shadow-md p-6 text-sm text-gray-600" role="status">
              Loading the Experimental tab…
            </div>
          }
        >
          <ExperimentalTab />
        </Suspense>
      );
    default:
      return <TabPlaceholder tabKey={def.key} />;
  }
};

export default DeviceWorkspaceTab;
